import { useRef, useState } from "preact/hooks";

import { AddHealthNoteSheet } from "../components/AddHealthNoteSheet";
import { AddWeightSheet } from "../components/AddWeightSheet";
import type { AnimalFormSheetEditFields } from "../components/AnimalFormSheet";
import { AnimalFormSheet } from "../components/AnimalFormSheet";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PhotoLightbox } from "../components/PhotoLightbox";
import { RecordDetailOverlay } from "../components/RecordDetailOverlay";
import { WeightSparkline } from "../components/WeightSparkline";
import { useAnimalHealth, useAnimalPhotos, useAnimalWeights } from "../hooks/useAnimals";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { attachmentThumbUrl } from "../lib/api";
import {
  ADD_A_NOTE_LABEL,
  ARCHIVE_ANIMAL_BUTTON_LABEL,
  ARCHIVE_ANIMAL_CONFIRM_BODY,
  ARCHIVE_ANIMAL_CONFIRM_LABEL,
  archiveAnimalConfirmTitle,
  birthDateLabel,
  CARD_PHOTO_BADGE_LABEL,
  CHOOSE_A_PHOTO_LABEL,
  COULD_NOT_SAVE_ERROR,
  EDITED_CHIP_LABEL,
  EDIT_BUTTON_LABEL,
  EDIT_HEALTH_NOTE_ARIA_LABEL,
  HEALTH_TIMELINE_EMPTY_HINT,
  LOG_A_WEIGHT_LABEL,
  paddockLabel,
  PHOTOS_EMPTY_HINT,
  PHOTO_DELETE_FAILED_MESSAGE,
  PHOTO_DELETED_MESSAGE,
  PHOTO_UPLOAD_FAILED_RETRY,
  RESTORE_ANIMAL_BUTTON_LABEL,
  SAVED_MESSAGE,
  TAKE_A_PHOTO_LABEL,
  UPLOAD_A_PHOTO_LABEL,
  WEIGHTS_EMPTY_HINT,
  WEIGHT_REMOVE_CONFIRM_BODY,
  WEIGHT_REMOVE_CONFIRM_LABEL,
  WEIGHT_REMOVE_CONFIRM_TITLE,
  healthEventTypeLabel,
  isEditedRecord,
  monthDayLabel,
  photoGridButtonLabel,
  weightLbLabel,
} from "../lib/recordsCopy";
import type {
  AnimalOut,
  AnimalWeightOut,
  AttachmentOut,
  HealthEventOut,
  HealthEventTypeValue,
} from "../types";

export interface AnimalDetailOverlayProps {
  animal: AnimalOut;
  /** Every authed role reaches this screen; `isAdmin` narrows the one-or-
   * more write affordances inside each tab (Overview's notes edit,
   * Care & health's "Add a note"/per-item edit, Weights' "+ Log a
   * weight"/"Remove," Photos' upload/"Remove") — every underlying
   * endpoint is admin/api only server-side, so a 403 is the real
   * enforcement boundary; this prop only decides what's rendered. */
  isAdmin: boolean;
  onClose: () => void;
  onNotify: (message: string) => void;
  /** Sprint-010 C4 (design-phase-c.md §2.3) — `PATCH /animals/{id}`'s
   * notes-only edit. Sprint-016 F1 AN-01 (ui-redesign-direction-
   * 2026-07-11.md item 1) retired the Overview tab's inline textarea that
   * used to call this — `notes` is now saved through `onSaveFields` (the
   * merged `AnimalFormSheet` edit flow) instead. Kept OPTIONAL, not
   * removed, purely so the many pre-existing test-suite callers that pass
   * a filler value here don't all need touching for an interface no
   * production caller relies on anymore; no code inside this component
   * reads it. */
  onSaveNotes?: (notes: string | null) => Promise<void>;
  /** Sprint-013 B2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
   * Decision 4.4) — the Photos tab's "Set as card photo"/"Remove as card
   * photo" affordance, threaded from `useAnimals().setPrimaryPhoto` — patches
   * the single-source-of-truth roster array in place (no refetch), so this
   * `animal` prop itself (derived via `.find()` in the parent) and the
   * grid behind this overlay both pick up the new `photoUrl` on the next
   * render. `attachmentId: null` clears the explicit selection. */
  onSetPrimaryPhoto: (attachmentId: string | null) => Promise<void>;
  /** Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5) — the
   * species combobox's `<datalist>` suggestions, computed once by the
   * parent (`AnimalsView`) from the whole fetched herd so the Edit sheet
   * offers the same suggestions the Add sheet does. Optional (defaults to
   * `[]`) so the many pre-existing callers of this component across the
   * test suite — which don't exercise the Edit sheet — don't all need a
   * value wired through. */
  speciesOptions?: string[];
  /** Sprint-013 C1 — the Edit sheet's submit: name/species/birth_date/
   * paddock, threaded from `useAnimals().updateAnimalFields` the same
   * in-place-patch way `onSetPrimaryPhoto` above works. Sprint-016 F1
   * AN-01 widens the field set the sheet (and this callback) carries to
   * also include `notes` (see `AnimalFormSheetEditFields`) — the merged
   * single entry point for every Overview-scope field, notes included.
   * Optional for the same reason `speciesOptions` above is — every
   * PRODUCTION caller (`AnimalsView`) always supplies it. */
  onSaveFields?: (fields: AnimalFormSheetEditFields) => Promise<void>;
  /** Sprint-013 C1 (ux-ruling-inactive-grid-2026-07-10.md — E1 PM ruling
   * item 5) — the Archive confirm's submit. The caller (`AnimalsView`) is
   * responsible for closing this overlay and showing the snackbar on
   * success, so it returns to the grid in its current Active/All toggle
   * state with no forced navigation from in here. Optional for the same
   * reason `speciesOptions`/`onSaveFields` above are. */
  onArchive?: () => Promise<void>;
  /** Sprint-016 F1 AN-02 (ux-audit-whole-app-2026-07-11.md — S1, PM
   * fix-batch #1) — the Restore action's submit, threaded from
   * `useAnimals().restoreAnimal` (mirrors `ProjectDetailOverlay`/
   * `ContactDetailOverlay`'s existing `onRestore` contract exactly,
   * `active: true` via the same `PATCH /animals/{id}` endpoint `onArchive`
   * already uses the other direction). No `ConfirmDialog` — restoring is
   * reversible/non-destructive. Optional for the same reason `onArchive`
   * is. */
  onRestore?: () => Promise<void>;
}

type AnimalTab = "overview" | "care-health" | "weights" | "photos";

const TABS: Array<{ key: AnimalTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "care-health", label: "Care & health" },
  { key: "weights", label: "Weights" },
  { key: "photos", label: "Photos" },
];

/**
 * Sprint-010 C3/C4 (design-phase-c.md §2) — an animal's record: four tabs
 * inside the SAME `.overlay-view` this screen has always been (no new
 * route, §0 — "extend the existing overlay"), reusing `.admin-subtabs`
 * verbatim (§2.2).
 *
 * Sprint-016 F1 AN-01/AN-03/AN-04 (ui-redesign-direction-2026-07-11.md
 * items 1-2, ratified by CD sign-off `cd-signoff-ui-audit-2026-07-11.md`
 * binding conditions 1-2) — converges Animal onto the Project/Contact
 * single-inline-"Edit" pattern. The header pencil `.icon-btn` is retired
 * (resolving AN-04's bare-glyph finding by removal, not by adding text to
 * it); the ONE edit entry point is now the Overview tab's body-inline
 * `.secondary-btn` "✎ Edit" (`OverviewPanel` below), which opens
 * `AnimalFormSheet` for every Overview-scope field, `notes` included
 * (merged in per AN-01 — the retired inline textarea's replacement). Per
 * CD binding condition 2, this Edit button renders on the Overview tab
 * ONLY — Care & health and Weights keep their own existing add
 * affordances, untouched. The Care & health tab's per-item edit sheet
 * (reuses `AddHealthNoteSheet`'s `mode="edit"`) is unaffected by this
 * change. The record-level "Edited" chip (§3.4, RESOLVED R-C0b-1) is
 * read-visible to every role that can already view the record, independent
 * of the edit affordance itself.
 */
export function AnimalDetailOverlay({
  animal,
  isAdmin,
  onClose,
  onNotify,
  onSetPrimaryPhoto,
  speciesOptions = [],
  onSaveFields,
  onArchive,
  onRestore,
}: AnimalDetailOverlayProps) {
  const [tab, setTab] = useState<AnimalTab>("overview");
  const [editingAnimal, setEditingAnimal] = useState(false);

  return (
    <RecordDetailOverlay
      onClose={onClose}
      title={animal.name}
      ariaLabel={`${animal.name}'s details`}
      backLabel="Back to Animals"
      isAdmin={false}
      isArchived={false}
      showEditedChip={false}
    >
      {/* Sprint-018 E3 (TD-038, ADR-003 Amendment 1 §D) — the shared
       * `RecordDetailOverlay` chrome (Edited chip / Edit button / Archived
       * tag / Archive↔Restore) renders UNCONDITIONALLY at the overlay
       * level, above/below this body slot — not scoped to any tab. CD
       * sign-off requires that chrome on the Overview tab ONLY, so the
       * props above are always the literal `false`/omitted values (NOT
       * this component's own `isAdmin`/`animal.active`/`isEditedRecord`),
       * which makes `RecordDetailOverlay`'s `showTopChrome` and
       * `showArchiveRestore` both evaluate `false` — zero extra chrome
       * DOM. `OverviewPanel` below keeps its own self-contained,
       * tab-scoped Edit/Archive/Restore/Edited-chip machinery unchanged.
       * NOTE — `RecordDetailOverlay`'s `isAdmin` prop (fixed `false`
       * above) is NOT `AnimalDetailOverlayProps.isAdmin` (below), which
       * continues to gate in-tab content normally; do not conflate them. */}
      <div class="admin-subtabs" role="tablist" aria-label={`${animal.name}'s record`}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            id={`animal-subtab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            aria-controls={`animal-subpanel-${t.key}`}
            class={`admin-subtab${tab === t.key ? " is-active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        id={`animal-subpanel-${tab}`}
        role="tabpanel"
        aria-labelledby={`animal-subtab-${tab}`}
        class="tab-panel"
      >
        {tab === "overview" ? (
          <OverviewPanel
            animal={animal}
            isAdmin={isAdmin}
            onNotify={onNotify}
            onArchive={onArchive}
            onRestore={onRestore}
            onStartEdit={() => setEditingAnimal(true)}
          />
        ) : tab === "care-health" ? (
          <CareHealthPanel animal={animal} isAdmin={isAdmin} onNotify={onNotify} />
        ) : tab === "weights" ? (
          <WeightsPanel
            animalId={animal.id}
            animalName={animal.name}
            isAdmin={isAdmin}
            onNotify={onNotify}
          />
        ) : (
          <PhotosPanel
            animalId={animal.id}
            animalName={animal.name}
            isAdmin={isAdmin}
            onNotify={onNotify}
            primaryPhotoAttachmentId={animal.primary_photo_attachment_id ?? null}
            onSetPrimaryPhoto={onSetPrimaryPhoto}
          />
        )}
      </div>

      {isAdmin ? (
        <AnimalFormSheet
          open={editingAnimal}
          mode="edit"
          animal={animal}
          speciesOptions={speciesOptions}
          onCancel={() => setEditingAnimal(false)}
          onSave={async (fields) => {
            await onSaveFields?.(fields);
            setEditingAnimal(false);
            onNotify(SAVED_MESSAGE);
          }}
        />
      ) : null}
    </RecordDetailOverlay>
  );
}

/** §7 — each panel's own `<h2 tabindex="-1">` gets focus on tab switch
 * (the "mount IS the mechanism" posture `useFocusHeadingOnMount` already
 * relies on elsewhere — every panel below is only ever mounted while its
 * own tab is active). `visuallyHidden` covers the Overview tab, whose own
 * mockup (§2.3) shows no repeated "Overview" heading text on screen —
 * the a11y focus target still needs to exist, just not visibly, mirroring
 * this app's existing `.sr-only` heading precedent (`styles.css`'s own
 * "a real heading for a view whose visible title lives elsewhere"
 * rationale, applied here to a tab panel instead of a whole screen). */
function PanelHeading({
  text,
  visuallyHidden = false,
}: {
  text: string;
  visuallyHidden?: boolean;
}) {
  const ref = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(ref);
  return (
    <h2 ref={ref} tabIndex={-1} class={visuallyHidden ? "sr-only" : "admin-section-heading"}>
      {text}
    </h2>
  );
}

/**
 * Sprint-016 F1 AN-01/AN-02/AN-03 (ui-redesign-direction-2026-07-11.md
 * items 1-2, ux-audit-whole-app-2026-07-11.md AN-02, CD sign-off binding
 * conditions 1-2) — the animal's "profile" tab. `notes` is now PURELY
 * display here (read-only text when set, the unchanged `.add-piece-btn`
 * "+ Add a note" CTA when empty) — both routes into the ONE merged edit
 * entry point (`onStartEdit`, opening `AnimalFormSheet` with every
 * Overview-scope field including notes), never a local edit-toggle. Per CD
 * binding condition 2, the "✎ Edit" trigger renders on this Overview tab
 * ONLY. Archive/Restore branch on `animal.active`, mirroring
 * `ProjectDetailOverlay`/`ContactDetailOverlay`'s existing pattern exactly
 * (AN-02 — animals were the one archivable record type with no Restore
 * path anywhere in the UI).
 */
function OverviewPanel({
  animal,
  isAdmin,
  onNotify,
  onArchive,
  onRestore,
  onStartEdit,
}: {
  animal: AnimalOut;
  isAdmin: boolean;
  onNotify: (message: string) => void;
  /** Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5) — the
   * Archive action's submit, `isAdmin`-gated. Rendered here since this is
   * the animal's "profile" tab — the trigger button + `ConfirmDialog`
   * live in this panel, but the parent overlay owns closing/notifying on
   * success (this panel only calls `onArchive()` and surfaces an inline
   * failure — it never closes itself). Optional for the same reason the
   * parent's own `onArchive` prop is (see `AnimalDetailOverlayProps`). */
  onArchive?: () => Promise<void>;
  /** Sprint-016 F1 AN-02 — the Restore action's submit, same posture as
   * `onArchive` above (mirrors `ProjectDetailOverlay`'s `handleRestore`).
   * No `ConfirmDialog` — restoring is reversible/non-destructive. */
  onRestore?: () => Promise<void>;
  /** Sprint-016 F1 AN-01/AN-03 — opens the parent's merged
   * `AnimalFormSheet` (`mode="edit"`), the ONE entry point for every
   * Overview-scope field, `notes` included. */
  onStartEdit: () => void;
}) {
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  async function handleConfirmArchive() {
    setArchiving(true);
    setConfirmArchive(false);
    try {
      // Success unmounts this whole overlay (the parent's `onArchive`
      // closes it and shows the snackbar) — no further local state to
      // set here; only the failure path needs to reset `archiving` so
      // the trigger becomes clickable again.
      await onArchive?.();
    } catch {
      setArchiving(false);
      onNotify(COULD_NOT_SAVE_ERROR);
    }
  }

  // No `ConfirmDialog` here — restoring is reversible/non-destructive (see
  // `lib/recordsCopy.ts`'s own note on why Restore skips the confirm step
  // `Archive` requires), mirrors `ProjectDetailOverlay.handleRestore`
  // exactly.
  async function handleRestore() {
    setRestoring(true);
    try {
      await onRestore?.();
    } catch {
      setRestoring(false);
      onNotify(COULD_NOT_SAVE_ERROR);
    }
  }

  const isEdited = isEditedRecord(animal.created_at, animal.updated_at);
  const isInactive = !animal.active;

  return (
    <div>
      <PanelHeading text="Overview" visuallyHidden />

      {isEdited || isAdmin ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isEdited ? <span class="edited-chip">{EDITED_CHIP_LABEL}</span> : null}
          {isAdmin ? (
            <button
              type="button"
              class="secondary-btn"
              style={{ width: "auto", flexShrink: 0, marginLeft: "auto" }}
              onClick={onStartEdit}
            >
              {EDIT_BUTTON_LABEL}
            </button>
          ) : null}
        </div>
      ) : null}

      {animal.species ? <p class="admin-row-sub">{animal.species}</p> : null}
      {animal.birth_date ? <p class="admin-row-sub">{birthDateLabel(animal.birth_date)}</p> : null}
      {animal.paddock ? <p class="admin-row-sub">{paddockLabel(animal.paddock)}</p> : null}

      {animal.notes ? (
        <>
          <h3 class="admin-field-label" style={{ marginTop: "18px" }}>
            Notes
          </h3>
          <p class="task-title">{animal.notes}</p>
        </>
      ) : isAdmin ? (
        <button
          type="button"
          class="add-piece-btn"
          style={{ marginTop: "18px" }}
          onClick={onStartEdit}
        >
          {ADD_A_NOTE_LABEL}
        </button>
      ) : null}

      {isAdmin ? (
        isInactive ? (
          <button
            type="button"
            class="secondary-btn"
            style={{ marginTop: "24px" }}
            disabled={restoring}
            onClick={() => void handleRestore()}
          >
            {restoring ? "Restoring…" : RESTORE_ANIMAL_BUTTON_LABEL}
          </button>
        ) : (
          <button
            type="button"
            class="secondary-btn"
            style={{ marginTop: "24px" }}
            disabled={archiving}
            onClick={() => setConfirmArchive(true)}
          >
            {archiving ? "Archiving…" : ARCHIVE_ANIMAL_BUTTON_LABEL}
          </button>
        )
      ) : null}

      {isAdmin ? (
        <ConfirmDialog
          open={confirmArchive}
          title={archiveAnimalConfirmTitle(animal.name)}
          body={ARCHIVE_ANIMAL_CONFIRM_BODY}
          confirmLabel={ARCHIVE_ANIMAL_CONFIRM_LABEL}
          destructive
          onConfirm={() => void handleConfirmArchive()}
          onCancel={() => setConfirmArchive(false)}
        />
      ) : null}
    </div>
  );
}

function CareHealthPanel({
  animal,
  isAdmin,
  onNotify,
}: {
  animal: AnimalOut;
  isAdmin: boolean;
  onNotify: (message: string) => void;
}) {
  const health = useAnimalHealth(animal.id);
  const [showAdd, setShowAdd] = useState(false);
  const [editingEvent, setEditingEvent] = useState<HealthEventOut | null>(null);
  // §8's TD-010-class rule — "have we ever successfully loaded data" is a
  // DIFFERENT boolean than "is a refetch currently in flight." Only the
  // former may show a full loading placeholder in place of content; a
  // background refresh (e.g. the one `addNote()` triggers on success)
  // must never remount the already-mounted timeline.
  const neverLoaded = health.events === null;

  return (
    <div>
      <PanelHeading text="Care & health" />

      {neverLoaded ? (
        health.error ? (
          <p class="error-hint" role="alert">
            {health.error}
          </p>
        ) : (
          <p class="loading-hint">Loading…</p>
        )
      ) : (
        <>
          {health.events!.length === 0 ? (
            <p class="empty-hint">{HEALTH_TIMELINE_EMPTY_HINT}</p>
          ) : (
            <ul class="health-timeline">
              {health.events!.map((event) => (
                <li class="health-timeline-item" key={event.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span class="health-type-tag">{healthEventTypeLabel(event.event_type)}</span>
                    {isEditedRecord(event.created_at, event.updated_at) ? (
                      <span class="edited-chip">{EDITED_CHIP_LABEL}</span>
                    ) : null}
                    {isAdmin ? (
                      <button
                        type="button"
                        class="icon-btn"
                        style={{ marginLeft: "auto", fontSize: "18px" }}
                        aria-label={EDIT_HEALTH_NOTE_ARIA_LABEL}
                        onClick={() => setEditingEvent(event)}
                      >
                        ✎
                      </button>
                    ) : null}
                  </div>
                  <p class="health-note">
                    {event.note} — {monthDayLabel(event.event_date)}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {health.error ? (
            <p class="error-hint" role="alert">
              {health.error}
            </p>
          ) : null}
        </>
      )}

      {isAdmin ? (
        <button
          type="button"
          class="add-piece-btn"
          style={{ marginTop: "18px" }}
          onClick={() => setShowAdd(true)}
        >
          ＋ Add a note
        </button>
      ) : null}

      {isAdmin ? (
        <AddHealthNoteSheet
          open={showAdd}
          animalName={animal.name}
          onCancel={() => setShowAdd(false)}
          onAdd={async (note, eventType) => {
            await health.addNote({ note, event_type: eventType });
            setShowAdd(false);
            onNotify(`Saved a note for ${animal.name}.`);
          }}
        />
      ) : null}

      {isAdmin ? (
        <AddHealthNoteSheet
          open={editingEvent !== null}
          mode="edit"
          animalName={animal.name}
          initialNote={editingEvent?.note ?? ""}
          initialEventType={(editingEvent?.event_type as HealthEventTypeValue) ?? null}
          initialEventDate={editingEvent?.event_date}
          onCancel={() => setEditingEvent(null)}
          onSave={async (note, eventType, eventDate) => {
            if (!editingEvent) return;
            await health.editNote(editingEvent.id, {
              note,
              event_type: eventType,
              event_date: eventDate,
            });
            setEditingEvent(null);
            onNotify(SAVED_MESSAGE);
          }}
        />
      ) : null}
    </div>
  );
}

function WeightsPanel({
  animalId,
  animalName,
  isAdmin,
  onNotify,
}: {
  animalId: string;
  animalName: string;
  isAdmin: boolean;
  onNotify: (message: string) => void;
}) {
  const weights = useAnimalWeights(animalId);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<AnimalWeightOut | null>(null);
  const neverLoaded = weights.weights === null;

  async function handleConfirmRemove() {
    if (!confirmTarget) return;
    const id = confirmTarget.id;
    setConfirmTarget(null);
    try {
      await weights.removeWeight(id);
      onNotify("Removed.");
    } catch {
      onNotify("Couldn't remove that — try again.");
    }
  }

  return (
    <div>
      <PanelHeading text="Weights" />

      {neverLoaded ? (
        weights.error ? (
          <p class="error-hint" role="alert">
            {weights.error}
          </p>
        ) : (
          <p class="loading-hint">Loading…</p>
        )
      ) : weights.weights!.length === 0 ? (
        <p class="empty-hint">{WEIGHTS_EMPTY_HINT}</p>
      ) : (
        <>
          <WeightSparkline weightsNewestFirst={weights.weights!} />
          <ul class="admin-list">
            {weights.weights!.map((w) => (
              <li key={w.id}>
                <div class="admin-row">
                  <span class="admin-row-main">
                    <span class="admin-row-name">{weightLbLabel(w.weight_lb)}</span>
                    <span class="admin-row-sub">{monthDayLabel(w.weighed_on)}</span>
                    {w.note ? <span class="admin-row-sub">{w.note}</span> : null}
                  </span>
                  {isAdmin ? (
                    <button
                      type="button"
                      // `.secondary-btn`'s base rule is `width: 100%` (a
                      // full-width sheet button everywhere else it's
                      // used) — inside this flex `.admin-row`, that would
                      // compute as 100% of the ROW, not the button's own
                      // intrinsic content, squeezing `.admin-row-main`'s
                      // text into a sliver. Fixed via the shared
                      // `.admin-row .secondary-btn` CSS rule (styles.css)
                      // rather than an inline override — same fix for
                      // every `.admin-row` that hosts a trailing
                      // `.secondary-btn` (passkey rows included).
                      class="secondary-btn"
                      onClick={() => setConfirmTarget(w)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {weights.error && !neverLoaded ? (
        <p class="error-hint" role="alert">
          {weights.error}
        </p>
      ) : null}

      {isAdmin ? (
        <button
          type="button"
          class="add-piece-btn"
          style={{ marginTop: "18px" }}
          onClick={() => setShowAdd(true)}
        >
          {LOG_A_WEIGHT_LABEL}
        </button>
      ) : null}

      {isAdmin ? (
        <AddWeightSheet
          open={showAdd}
          animalName={animalName}
          onCancel={() => setShowAdd(false)}
          onAdd={async (weightLb, weighedOn, note) => {
            await weights.addWeight({ weight_lb: weightLb, weighed_on: weighedOn, note });
            setShowAdd(false);
            onNotify("Saved.");
          }}
        />
      ) : null}

      {isAdmin ? (
        <ConfirmDialog
          open={confirmTarget !== null}
          title={WEIGHT_REMOVE_CONFIRM_TITLE}
          body={WEIGHT_REMOVE_CONFIRM_BODY}
          confirmLabel={WEIGHT_REMOVE_CONFIRM_LABEL}
          destructive
          onConfirm={() => void handleConfirmRemove()}
          onCancel={() => setConfirmTarget(null)}
        />
      ) : null}
    </div>
  );
}

function PhotosPanel({
  animalId,
  animalName,
  isAdmin,
  onNotify,
  primaryPhotoAttachmentId,
  onSetPrimaryPhoto,
}: {
  animalId: string;
  animalName: string;
  isAdmin: boolean;
  onNotify: (message: string) => void;
  /** Sprint-013 B2 — the animal's RESOLVED card photo (`animal.
   * primary_photo_attachment_id`, §0's "explicit-else-newest" rule) —
   * `null` when no photo has resolved (no photos at all). Drives both
   * the grid tile's badge and the lightbox's indicator/toggle-button
   * state. */
  primaryPhotoAttachmentId: string | null;
  onSetPrimaryPhoto: (attachmentId: string | null) => Promise<void>;
}) {
  const photos = useAnimalPhotos(animalId);
  const isDesktop = useIsDesktop();
  const [lightboxPhoto, setLightboxPhoto] = useState<AttachmentOut | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const neverLoaded = photos.photos === null;
  const totalCount = photos.photos?.length ?? 0;

  function handleFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    // Reset the input's own value so selecting the SAME file again still
    // fires a fresh `change` event (native <input type="file"> behavior —
    // it never fires a second time for an identical selection otherwise).
    input.value = "";
    if (file) photos.startUpload(file);
  }

  const isGridEmpty =
    !neverLoaded && (photos.photos?.length ?? 0) === 0 && photos.uploads.length === 0;

  return (
    <div>
      <PanelHeading text="Photos" />

      {isAdmin ? (
        isDesktop ? (
          <>
            <button
              type="button"
              class="secondary-btn"
              onClick={() => desktopInputRef.current?.click()}
            >
              {UPLOAD_A_PHOTO_LABEL}
            </button>
            <input
              ref={desktopInputRef}
              type="file"
              accept="image/*"
              aria-hidden="true"
              tabIndex={-1}
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </>
        ) : (
          <div class="photo-upload-buttons">
            <button
              type="button"
              class="secondary-btn"
              onClick={() => cameraInputRef.current?.click()}
            >
              {TAKE_A_PHOTO_LABEL}
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              aria-hidden="true"
              tabIndex={-1}
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
            <button
              type="button"
              class="secondary-btn"
              onClick={() => galleryInputRef.current?.click()}
            >
              {CHOOSE_A_PHOTO_LABEL}
            </button>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              aria-hidden="true"
              tabIndex={-1}
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
          </div>
        )
      ) : null}

      {neverLoaded ? (
        photos.error ? (
          <p class="error-hint" role="alert">
            {photos.error}
          </p>
        ) : (
          <p class="loading-hint">Loading…</p>
        )
      ) : isGridEmpty ? (
        <p class="empty-hint">{PHOTOS_EMPTY_HINT}</p>
      ) : (
        <ul class="photo-grid">
          {photos.uploads.map((tile) =>
            tile.status === "uploading" ? (
              <li key={tile.localId}>
                <div class="photo-tile photo-tile-uploading" aria-hidden="true" />
              </li>
            ) : (
              <li key={tile.localId}>
                <button
                  type="button"
                  class="photo-tile photo-tile-failed"
                  onClick={() => photos.retryUpload(tile.localId)}
                >
                  <span aria-hidden="true">↻</span>
                  <span>{tile.errorMessage ?? PHOTO_UPLOAD_FAILED_RETRY}</span>
                </button>
              </li>
            ),
          )}
          {(photos.photos ?? []).map((photo, index) => {
            const isPrimary = photo.id === primaryPhotoAttachmentId;
            return (
              <li key={photo.id}>
                <button
                  type="button"
                  class="photo-tile"
                  aria-label={photoGridButtonLabel(animalName, index + 1, totalCount, isPrimary)}
                  onClick={() => setLightboxPhoto(photo)}
                >
                  {isPrimary ? (
                    // aria-hidden — the button's own aria-label above
                    // already carries "current card photo" (never
                    // conveyed by this visual-only badge alone, matching
                    // design-spec-animal-cards-2026-07-10.md §0's rule
                    // for the card grid's own chips).
                    <span class="photo-tile-primary-badge" aria-hidden="true">
                      {CARD_PHOTO_BADGE_LABEL}
                    </span>
                  ) : null}
                  <img src={attachmentThumbUrl(photo.id)} alt="" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {photos.error && !neverLoaded ? (
        <p class="error-hint" role="alert">
          {photos.error}
        </p>
      ) : null}

      <PhotoLightbox
        photo={lightboxPhoto}
        animalName={animalName}
        isAdmin={isAdmin}
        isPrimary={lightboxPhoto !== null && lightboxPhoto.id === primaryPhotoAttachmentId}
        onClose={() => setLightboxPhoto(null)}
        onRemove={async (id) => {
          try {
            await photos.removePhoto(id);
            onNotify(PHOTO_DELETED_MESSAGE);
            setLightboxPhoto(null);
          } catch {
            onNotify(PHOTO_DELETE_FAILED_MESSAGE);
          }
        }}
        onSetPrimaryPhoto={async (attachmentId) => {
          try {
            await onSetPrimaryPhoto(attachmentId);
            onNotify(SAVED_MESSAGE);
          } catch {
            onNotify(COULD_NOT_SAVE_ERROR);
          }
        }}
      />
    </div>
  );
}
